import { defineStore } from 'pinia'
import { ref } from 'vue'
import { inboxRepository } from '@/infrastructure'
import { bellNotices, loadBell } from '@/application/inbox/loadBell'
import { markNotificationsRead } from '@/application/inbox/markNotificationsRead'
import type { InboxNotification, PendingSummary } from '@/domain/inbox/Inbox'
import { useUserStore } from '@/store/user'

export const BELL_REFRESH_MS = 60_000

export const useInboxStore = defineStore('inbox', () => {
  const summary = ref<PendingSummary | null>(null)
  const notifications = ref<InboxNotification[]>([])
  const notificationsCount = ref(0)
  const unreadCount = ref(0)
  const panelOpen = ref(false)
  let timer: ReturnType<typeof setInterval> | null = null
  let watchers = 0
  let latestRefresh = 0
  let loadedFor: number | null | undefined

  async function refresh(): Promise<void> {
    if (document.visibilityState !== 'visible') return
    const userId = useUserStore().id ?? null
    if (userId !== loadedFor) clear()
    loadedFor = userId
    const current = ++latestRefresh
    const data = await loadBell(inboxRepository)
    if (current !== latestRefresh) return
    if (data.pending.status === 'fulfilled') summary.value = data.pending.value
    const latest = data.notifications.status === 'fulfilled' ? data.notifications.value : null
    const unread = data.unread.status === 'fulfilled' ? data.unread.value : null
    if (latest) notificationsCount.value = latest.count
    if (unread) unreadCount.value = unread.count
    if (latest || unread) notifications.value = bellNotices(unread?.notifications ?? [], latest?.notifications ?? [])
  }

  function onVisibility(): void {
    if (document.visibilityState === 'visible') void refresh()
  }

  function start(): void {
    watchers += 1
    if (watchers > 1) return
    void refresh()
    timer = setInterval(() => void refresh(), BELL_REFRESH_MS)
    document.addEventListener('visibilitychange', onVisibility)
  }

  function stop(): void {
    watchers = Math.max(0, watchers - 1)
    if (watchers > 0) return
    if (timer) clearInterval(timer)
    timer = null
    document.removeEventListener('visibilitychange', onVisibility)
    panelOpen.value = false
  }

  async function markShownRead(): Promise<void> {
    const shown = notifications.value.filter((n) => n.readAt === null).map((n) => n.id)
    if (shown.length === 0) return
    try {
      await markNotificationsRead(inboxRepository, shown)
    } catch {
      return
    }
    const now = new Date().toISOString()
    notifications.value = notifications.value.map((n) => (shown.includes(n.id) ? { ...n, readAt: now } : n))
    unreadCount.value = Math.max(0, unreadCount.value - shown.length)
    void refresh()
  }

  function openPanel(): void {
    panelOpen.value = true
  }

  function clear(): void {
    latestRefresh++
    summary.value = null
    notifications.value = []
    notificationsCount.value = 0
    unreadCount.value = 0
    panelOpen.value = false
    loadedFor = undefined
  }

  return { summary, notifications, notificationsCount, unreadCount, panelOpen, refresh, start, stop, markShownRead, openPanel, clear }
})
